import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Button,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
  Container,
  Flex,
  Icon,
  Grid,
  GridItem,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
} from '@chakra-ui/react';
import { FaCreditCard, FaCheckCircle, FaFileInvoiceDollar, FaLock, FaVial, FaStethoscope, FaSyringe, FaTimes } from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// Embedded checkout form component
function CheckoutForm({ amount, formatCurrency, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setPaying(true);
    setPayError(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href + (window.location.href.includes('?') ? '&' : '?') + 'payment=success',
      },
      redirect: 'if_required',
    });

    if (error) {
      setPayError(error.message);
      setPaying(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess();
    } else {
      setPaying(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />

        {payError && (
          <Alert status="error" borderRadius="md" fontSize="sm">
            <AlertIcon />
            {payError}
          </Alert>
        )}

        <Button
          type="submit"
          bgGradient="linear(to-r, #276749, #38a169)"
          color="white"
          size="lg"
          width="full"
          leftIcon={<Icon as={FaCreditCard} />}
          isLoading={paying || !stripe}
          loadingText="Processing payment..."
          fontSize="md"
          py={7}
          borderRadius="xl"
          _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
          _active={{ transform: 'translateY(0)', boxShadow: 'md' }}
          transition="all 0.2s"
          boxShadow="md"
          isDisabled={!stripe || !elements}
        >
          Pay {formatCurrency(amount)}
        </Button>

        <HStack spacing={1.5} justify="center" pb={1}>
          <Icon as={FaLock} color="gray.400" boxSize={3} />
          <Text fontSize="xs" color="gray.400">
            Secure payment powered by Stripe
          </Text>
        </HStack>
      </VStack>
    </form>
  );
}

export default function PublicInvoicePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const { isOpen: isPaymentOpen, onOpen: onPaymentOpen, onClose: onPaymentClose } = useDisclosure();

  const paymentResult = searchParams.get('payment');

  const tokensParam = searchParams.get('tokens');
  const tokens = token ? [token] : (tokensParam ? tokensParam.split(',').filter(Boolean) : []);

  useEffect(() => {
    if (tokens.length > 0) fetchInvoices();
  }, [token, tokensParam]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const results = await Promise.all(
        tokens.map(async (t) => {
          const res = await fetch(`${apiBase}/api/public-invoice?token=${t}`);
          if (!res.ok) return null;
          return res.json();
        })
      );
      const valid = results.filter(Boolean);
      if (valid.length === 0) throw new Error('Invoice not found');
      setInvoices(valid);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShowPayment = useCallback(async () => {
    setPaymentLoading(true);
    setError(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/create-invoice-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokens }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error?.message || 'Failed to initialize payment');
      setClientSecret(result.clientSecret);
      onPaymentOpen();
    } catch (err) {
      setError(err.message);
    } finally {
      setPaymentLoading(false);
    }
  }, [tokens, onPaymentOpen]);

  const handlePaymentSuccess = useCallback(() => {
    setPaymentSuccess(true);
    onPaymentClose();
    setClientSecret(null);
    // Refresh invoices to show updated status
    setTimeout(() => fetchInvoices(), 2000);
  }, [onPaymentClose]);

  const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="green.500" thickness="4px" />
          <Text color="gray.500" fontSize="sm">Loading invoice...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error && invoices.length === 0) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <Box bg="white" p={10} borderRadius="2xl" boxShadow="xl" maxW="md" w="full" textAlign="center">
          <Icon as={FaFileInvoiceDollar} boxSize={14} color="gray.200" mb={4} />
          <Heading size="md" mb={2} color="gray.700">Invoice Not Found</Heading>
          <Text color="gray.500" fontSize="sm">{error}</Text>
          <Text color="gray.400" fontSize="xs" mt={4}>
            If you believe this is an error, please contact support.
          </Text>
        </Box>
      </Flex>
    );
  }

  const allPaid = paymentSuccess || invoices.every(inv => inv.payment_status === 'paid');
  const grandTotal = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const isMultiple = invoices.length > 1;
  const labName = invoices[0]?.lab_name || 'Client';
  const labEmail = invoices[0]?.lab_email || null;
  const totalLineItems = invoices.reduce((sum, inv) => sum + (inv.line_items?.length || 0), 0);

  const stripeOptions = clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#38a169',
        colorBackground: '#ffffff',
        colorText: '#1a202c',
        colorDanger: '#e53e3e',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        borderRadius: '8px',
        spacingUnit: '4px',
      },
    },
  } : null;

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 4, md: 8 }}>
      <Container maxW="800px" px={{ base: 3, md: 6 }}>
        {/* Payment result banner */}
        {(paymentResult === 'success' || paymentSuccess) && (
          <Alert status="success" borderRadius="lg" mb={6} boxShadow="sm">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Payment received!</Text>
              <Text fontSize="sm">Thank you for your payment. A confirmation has been sent.</Text>
            </Box>
          </Alert>
        )}
        {paymentResult === 'cancel' && (
          <Alert status="warning" borderRadius="lg" mb={6} boxShadow="sm">
            <AlertIcon />
            <Text>Payment was cancelled. You can try again below.</Text>
          </Alert>
        )}

        {/* Main Invoice Card */}
        <Box bg="white" borderRadius="2xl" boxShadow="xl" overflow="hidden" border="1px solid" borderColor="gray.100">

          {/* Header */}
          <Box
            bgGradient="linear(to-r, #276749, #38a169)"
            color="white"
            px={{ base: 6, md: 10 }}
            py={8}
            position="relative"
            overflow="hidden"
          >
            {/* Subtle pattern overlay */}
            <Box
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              left={0}
              opacity={0.05}
              bgImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)"
              bgSize="20px 20px"
            />
            <Flex justify="space-between" align="flex-start" position="relative">
              <VStack align="start" spacing={2}>
                <Text fontSize="lg" fontWeight="800" letterSpacing="0.5px">
                  AtHomeBloodDraw.com
                </Text>
                <Heading size="xl" fontWeight="800" letterSpacing="-0.5px">
                  {isMultiple ? 'INVOICES' : 'INVOICE'}
                </Heading>
                <Text fontSize="sm" opacity={0.7}>
                  Mobile Phlebotomy Services
                </Text>
              </VStack>
              <VStack align="end" spacing={2} mt={1}>
                {isMultiple ? (
                  <>
                    <Badge bg="whiteAlpha.200" color="white" fontSize="sm" px={3} py={1} borderRadius="full">
                      {invoices.length} Invoices
                    </Badge>
                    <Text fontSize="xs" opacity={0.6}>
                      {totalLineItems} draws total
                    </Text>
                  </>
                ) : (
                  <>
                    <Text fontSize="xs" opacity={0.6} textTransform="uppercase" letterSpacing="wider">
                      Invoice No.
                    </Text>
                    <Text fontSize="2xl" fontWeight="800" letterSpacing="1px">
                      {invoices[0]?.invoice_number || '—'}
                    </Text>
                  </>
                )}
                <Text fontSize="xs" opacity={0.6}>
                  Issued {formatDate(invoices[0]?.created_at)}
                </Text>
              </VStack>
            </Flex>
          </Box>

          {/* Status Bar */}
          <Box
            bg={allPaid ? 'green.50' : 'orange.50'}
            px={{ base: 6, md: 10 }}
            py={3}
            borderBottom="1px solid"
            borderColor={allPaid ? 'green.100' : 'orange.100'}
          >
            <Flex justify="space-between" align="center">
              <HStack spacing={2}>
                <Icon as={allPaid ? FaCheckCircle : FaCreditCard} color={allPaid ? 'green.500' : 'orange.500'} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold" color={allPaid ? 'green.700' : 'orange.700'}>
                  {allPaid ? 'All Payments Received' : 'Payment Required'}
                </Text>
              </HStack>
              <Text fontSize="xl" fontWeight="800" color={allPaid ? 'green.700' : 'orange.700'}>
                {formatCurrency(grandTotal)}
              </Text>
            </Flex>
          </Box>

          {/* Bill To Section */}
          <Box px={{ base: 6, md: 10 }} py={6}>
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>
                  Bill To
                </Text>
                <Text fontSize="lg" fontWeight="700" color="gray.800">
                  {labName}
                </Text>
                {labEmail && (
                  <Text fontSize="sm" color="gray.500" mt={0.5}>{labEmail}</Text>
                )}
              </Box>
              <Box textAlign="right">
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>
                  Status
                </Text>
                <Badge
                  colorScheme={allPaid ? 'green' : 'red'}
                  fontSize="sm"
                  px={4}
                  py={1.5}
                  borderRadius="full"
                  fontWeight="700"
                >
                  {allPaid ? 'PAID' : 'UNPAID'}
                </Badge>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.100" />

          {/* Invoice Groups */}
          {invoices.map((inv, invIdx) => (
            <Box key={invIdx}>
              {/* Invoice Header (for multi-invoice) */}
              {isMultiple && (
                <Box
                  px={{ base: 6, md: 10 }}
                  py={4}
                  bg="gray.50"
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  {...(invIdx > 0 ? { borderTop: '1px solid', borderTopColor: 'gray.200' } : {})}
                >
                  <Flex justify="space-between" align="center">
                    <HStack spacing={3}>
                      <Box
                        bg="green.500"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="md"
                        fontSize="xs"
                        fontWeight="800"
                      >
                        {inv.invoice_number || `INV-${invIdx + 1}`}
                      </Box>
                      {inv.batch_number && (
                        <Text fontSize="xs" color="gray.500">{inv.batch_number}</Text>
                      )}
                      <Badge
                        colorScheme={inv.payment_status === 'paid' ? 'green' : 'orange'}
                        fontSize="xs"
                        borderRadius="full"
                        px={2}
                      >
                        {(inv.payment_status || 'unpaid').toUpperCase()}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" fontWeight="700" color="gray.600">
                      {formatCurrency(inv.total_amount)}
                    </Text>
                  </Flex>
                </Box>
              )}

              {/* Line Items */}
              <Box px={{ base: 6, md: 10 }} py={5}>
                {/* Column Headers */}
                {invIdx === 0 && (
                  <Grid
                    templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                    gap={4}
                    pb={3}
                    mb={4}
                    borderBottom="2px solid"
                    borderColor="gray.200"
                  >
                    <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                      Service Details
                    </Text>
                    <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" display={{ base: 'none', md: 'block' }}>
                      Collection Date
                    </Text>
                    <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" textAlign="right">
                      Amount
                    </Text>
                  </Grid>
                )}

                <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                  {inv.line_items && inv.line_items.map((item, i) => (
                    <Grid
                      key={i}
                      templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                      gap={4}
                      py={4}
                      _hover={{ bg: 'gray.50' }}
                      mx={-3}
                      px={3}
                      borderRadius="lg"
                      transition="background 0.15s"
                    >
                      <GridItem>
                        <HStack spacing={3} align="flex-start">
                          <Box
                            bg="green.50"
                            borderRadius="lg"
                            p={2}
                            mt={0.5}
                            flexShrink={0}
                          >
                            <Icon as={FaVial} color="green.400" boxSize={3.5} />
                          </Box>
                          <Box flex={1}>
                            <Text fontSize="sm" fontWeight="700" color="gray.800">
                              {item.patient_name}
                            </Text>
                            <HStack spacing={3} mt={1.5} flexWrap="wrap">
                              {item.patient_dob && (
                                <HStack spacing={1}>
                                  <Text fontSize="xs" color="gray.400">DOB:</Text>
                                  <Text fontSize="xs" color="gray.600" fontWeight="500">{formatDate(item.patient_dob)}</Text>
                                </HStack>
                              )}
                            </HStack>
                            {(item.doctor_name || item.phlebotomist_name) && (
                              <HStack spacing={4} mt={1.5} flexWrap="wrap">
                                {item.doctor_name && (
                                  <HStack spacing={1}>
                                    <Icon as={FaStethoscope} color="gray.300" boxSize={2.5} />
                                    <Text fontSize="xs" color="gray.500">{item.doctor_name}</Text>
                                  </HStack>
                                )}
                                {item.phlebotomist_name && (
                                  <HStack spacing={1}>
                                    <Icon as={FaSyringe} color="gray.300" boxSize={2.5} />
                                    <Text fontSize="xs" color="gray.500">{item.phlebotomist_name}</Text>
                                  </HStack>
                                )}
                              </HStack>
                            )}
                            {item.tests && item.tests.length > 0 && (
                              <HStack spacing={1.5} mt={2} flexWrap="wrap">
                                {item.tests.map((test, ti) => (
                                  <Badge
                                    key={ti}
                                    bg="green.50"
                                    color="green.600"
                                    fontSize="10px"
                                    fontWeight="600"
                                    px={2}
                                    py={0.5}
                                    borderRadius="full"
                                    border="1px solid"
                                    borderColor="green.100"
                                  >
                                    {test}
                                  </Badge>
                                ))}
                              </HStack>
                            )}
                            {item.diagnosis && (
                              <HStack spacing={1} mt={1}>
                                <Text fontSize="xs" color="gray.400" fontWeight="600">ICD:</Text>
                                <Text fontSize="xs" color="gray.500">{item.diagnosis}</Text>
                              </HStack>
                            )}
                            {/* Mobile date */}
                            <Text fontSize="xs" color="gray.400" mt={1.5} display={{ base: 'block', md: 'none' }}>
                              {item.collection_date ? formatDate(item.collection_date) : '—'}
                            </Text>
                          </Box>
                        </HStack>
                      </GridItem>
                      <GridItem display={{ base: 'none', md: 'flex' }} alignItems="flex-start" pt={1}>
                        <Text fontSize="sm" color="gray.600">
                          {item.collection_date ? formatDate(item.collection_date) : '—'}
                        </Text>
                      </GridItem>
                      <GridItem textAlign="right" pt={1}>
                        <Text fontSize="sm" fontWeight="700" color="gray.800">
                          {formatCurrency(item.line_total)}
                        </Text>
                        {item.admin_fee > 0 && item.draw_fee > 0 && (
                          <Text fontSize="xs" color="gray.400" mt={0.5}>
                            {formatCurrency(item.draw_fee)} + {formatCurrency(item.admin_fee)}
                          </Text>
                        )}
                      </GridItem>
                    </Grid>
                  ))}

                  {/* Shipping Fee */}
                  {inv.ship_fee > 0 && (
                    <Grid
                      templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                      gap={4}
                      py={3}
                    >
                      <GridItem>
                        <Text fontSize="sm" color="gray.600">Shipping Fee</Text>
                      </GridItem>
                      <GridItem display={{ base: 'none', md: 'block' }} />
                      <GridItem textAlign="right">
                        <Text fontSize="sm" fontWeight="600" color="gray.700">{formatCurrency(inv.ship_fee)}</Text>
                      </GridItem>
                    </Grid>
                  )}
                </VStack>

                {/* Invoice Subtotal (multi-invoice) */}
                {isMultiple && (
                  <Grid
                    templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                    gap={4}
                    mt={3}
                    pt={3}
                    borderTop="2px solid"
                    borderColor="gray.200"
                  >
                    <GridItem>
                      <Text fontSize="sm" fontWeight="700" color="gray.600">
                        Subtotal — {inv.invoice_number || `Invoice ${invIdx + 1}`}
                      </Text>
                    </GridItem>
                    <GridItem display={{ base: 'none', md: 'block' }} />
                    <GridItem textAlign="right">
                      <Text fontSize="sm" fontWeight="800" color="gray.700">{formatCurrency(inv.total_amount)}</Text>
                    </GridItem>
                  </Grid>
                )}
              </Box>
            </Box>
          ))}

          {/* Grand Total */}
          <Box
            bg="gray.50"
            px={{ base: 6, md: 10 }}
            py={6}
            borderTop="2px solid"
            borderColor="gray.200"
          >
            <Flex justify="flex-end" align="center">
              <VStack align="end" spacing={1}>
                <HStack spacing={6}>
                  <Text fontSize="md" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    {isMultiple ? 'Grand Total' : 'Total Due'}
                  </Text>
                  <Text fontSize="3xl" fontWeight="800" color={allPaid ? 'green.600' : 'gray.800'}>
                    {formatCurrency(grandTotal)}
                  </Text>
                </HStack>
                {isMultiple && (
                  <Text fontSize="xs" color="gray.400">
                    {invoices.length} invoices &middot; {totalLineItems} blood draws
                  </Text>
                )}
              </VStack>
            </Flex>
          </Box>

          {/* Payment Section */}
          <Box px={{ base: 6, md: 10 }} py={8} bg="white">
            {allPaid ? (
              <Box
                bg="green.50"
                borderRadius="xl"
                p={6}
                textAlign="center"
                border="1px solid"
                borderColor="green.100"
              >
                <Icon as={FaCheckCircle} color="green.400" boxSize={8} mb={3} />
                <Text fontWeight="700" color="green.700" fontSize="lg">Payment Complete</Text>
                <Text fontSize="sm" color="green.600" mt={1}>
                  Thank you for your payment. No further action is needed.
                </Text>
              </Box>
            ) : (
              <VStack spacing={4}>
                {error && (
                  <Alert status="error" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
                <Button
                  bgGradient="linear(to-r, #276749, #38a169)"
                  color="white"
                  size="lg"
                  width="full"
                  leftIcon={<Icon as={FaCreditCard} />}
                  onClick={handleShowPayment}
                  isLoading={paymentLoading}
                  loadingText="Preparing payment..."
                  fontSize="md"
                  py={7}
                  borderRadius="xl"
                  _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  _active={{ transform: 'translateY(0)', boxShadow: 'md' }}
                  transition="all 0.2s"
                  boxShadow="md"
                >
                  Pay {formatCurrency(grandTotal)}
                </Button>
                <HStack spacing={1.5} justify="center">
                  <Icon as={FaLock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.400">
                    Secure payment powered by Stripe
                  </Text>
                </HStack>
              </VStack>
            )}
          </Box>

          {/* Payment Modal */}
          <Modal isOpen={isPaymentOpen} onClose={onPaymentClose} isCentered size="md" closeOnOverlayClick={false}>
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
              <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
                <Flex justify="space-between" align="center">
                  <VStack align="start" spacing={0}>
                    <Text color="white" fontSize="lg" fontWeight="700">Payment</Text>
                    <Text color="whiteAlpha.800" fontSize="sm">{formatCurrency(grandTotal)} due</Text>
                  </VStack>
                  <Box
                    as="button"
                    onClick={onPaymentClose}
                    color="whiteAlpha.800"
                    _hover={{ color: 'white' }}
                    p={1}
                  >
                    <Icon as={FaTimes} boxSize={4} />
                  </Box>
                </Flex>
              </Box>
              <ModalBody px={6} py={5}>
                {clientSecret && stripePromise ? (
                  <Elements stripe={stripePromise} options={stripeOptions}>
                    <CheckoutForm
                      amount={grandTotal}
                      formatCurrency={formatCurrency}
                      onSuccess={handlePaymentSuccess}
                    />
                  </Elements>
                ) : (
                  <Flex justify="center" py={8}>
                    <Spinner size="lg" color="green.500" />
                  </Flex>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Footer */}
          <Box
            bg="gray.50"
            px={{ base: 6, md: 10 }}
            py={5}
            borderTop="1px solid"
            borderColor="gray.100"
          >
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
              <Text fontSize="xs" color="gray.500" fontWeight="600">
                AtHomeBloodDraw.com
              </Text>
              <Text fontSize="xs" color="gray.400">
                Questions? Contact support@athomeblooddraw.com
              </Text>
            </Flex>
          </Box>
        </Box>

        {/* Bottom branding */}
        <Text textAlign="center" fontSize="xs" color="gray.400" mt={6}>
          Mobile Phlebotomy Services
        </Text>
      </Container>
    </Box>
  );
}
